import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, ClipboardCheck, Play, Trash2, Building2, Copy, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePlanLimit, PlanLimitBanner } from "@/components/PlanLimitGuard";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { getPerms } from "@/lib/permissions";

const CATEGORY_LABEL = {
  operacao: "Operação",
  abertura: "Abertura",
  fechamento: "Fechamento",
  appcc: "APPCC",
  limpeza: "Limpeza",
  recebimento: "Recebimento",
  controle_estoque: "Controle de estoque",
};

const FREQUENCY_LABEL = {
  daily: "Diário",
  weekly: "Semanal",
  monthly: "Mensal",
  shift: "Por turno",
  custom: "Customizado",
};

const WEEKDAY_LABEL = [
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
  "Domingo",
];

function frequencyText(t) {
  let txt = FREQUENCY_LABEL[t.frequency] || t.frequency;
  if (t.frequency === "weekly" && t.weekday != null) {
    txt += ` · ${WEEKDAY_LABEL[t.weekday] || t.weekday}`;
  }
  if (t.frequency === "monthly" && t.day_of_month != null) {
    txt += ` · dia ${t.day_of_month}`;
  }
  if (["daily", "weekly", "monthly"].includes(t.frequency) && t.time_of_day) {
    txt += ` · ${t.time_of_day}`;
  }
  return txt;
}

function TemplatesTable({ items, catLabels, canManage, onStart, onRemove, onEdit, onCopy }) {
  return (
    <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-stone-50">
            <TableHead className="font-body">Nome</TableHead>
            <TableHead className="font-body">Categoria</TableHead>
            <TableHead className="font-body">Frequência</TableHead>
            <TableHead className="font-body">Perguntas</TableHead>
            <TableHead className="font-body">Criado em</TableHead>
            <TableHead className="font-body text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-stone-500 py-10">
                <ClipboardCheck className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                Nenhum template criado
              </TableCell>
            </TableRow>
          )}
          {items.map((t) => (
            <TableRow key={t.id} data-testid={`checklist-row-${t.id}`}>
              <TableCell>
                {canManage ? (
                  <button
                    type="button"
                    onClick={() => onEdit(t.id)}
                    className="font-display font-semibold text-emerald-900 hover:underline inline-flex items-center gap-1.5 text-left"
                    data-testid={`checklist-edit-${t.id}`}
                  >
                    {t.name}
                    <Pencil className="h-3 w-3 opacity-40" />
                  </button>
                ) : (
                  <div className="font-display font-semibold">{t.name}</div>
                )}
                <div className="text-xs text-stone-500 font-body line-clamp-1">{t.description}</div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="border-emerald-200 text-emerald-800">
                  {catLabels[t.category] || t.category}
                </Badge>
              </TableCell>
              <TableCell className="font-body text-sm">{frequencyText(t)}</TableCell>
              <TableCell className="font-body text-sm">{t.questions?.length || 0}</TableCell>
              <TableCell className="font-body text-sm">{formatDate(t.created_at)}</TableCell>
              <TableCell className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  className="mr-2"
                  onClick={() => onStart(t.id)}
                  data-testid={`checklist-start-${t.id}`}
                >
                  <Play className="h-3.5 w-3.5 mr-1" /> Executar
                </Button>
                {canManage && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Duplicar checklist"
                      onClick={() => onCopy(t.id)}
                      data-testid={`checklist-copy-${t.id}`}
                    >
                      <Copy className="h-4 w-4 text-stone-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemove(t.id)}
                      data-testid={`checklist-delete-${t.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function Checklists() {
  const { user } = useAuth();
  const perms = getPerms(user?.role);
  const canManage = perms.createTemplates;
  const isCompanyAdmin = ["super_admin", "company_admin"].includes(user?.role);
  const limitInfo = usePlanLimit("checklists");
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [catLabels, setCatLabels] = useState(CATEGORY_LABEL);
  const [catFilter, setCatFilter] = useState("all");
  const [freqFilter, setFreqFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [tpl, cats, un] = await Promise.all([
        api.get("/checklists/templates"),
        api.get("/checklists/categories").catch(() => ({ data: [] })),
        api.get("/units").catch(() => ({ data: [] })),
      ]);
      setItems(tpl.data);
      setUnits(un.data || []);
      if (cats.data?.length) {
        const map = { ...CATEGORY_LABEL };
        for (const c of cats.data) map[c.key] = c.label;
        setCatLabels(map);
      }
    } catch (err) {
      console.error("Failed to load templates", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!confirm("Excluir este template?")) return;
    try {
      await api.delete(`/checklists/templates/${id}`);
      toast.success("Template removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir");
    }
  };

  // FASE 19 — edit + duplicate
  const editTemplate = (id) => navigate(`/checklists/${id}/edit`);
  const copyTemplate = async (id) => {
    try {
      const { data } = await api.post(`/checklists/templates/${id}/duplicate`);
      toast.success(`Cópia criada: ${data.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao duplicar");
    }
  };

  const startExecution = async (templateId) => {
    try {
      const { data } = await api.post("/checklists/executions", {
        template_id: templateId,
      });
      navigate(`/executions/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao iniciar");
    }
  };

  // Filtros rápidos por categoria e frequência
  const filtered = useMemo(
    () =>
      items.filter(
        (t) =>
          (catFilter === "all" || t.category === catFilter) &&
          (freqFilter === "all" || t.frequency === freqFilter)
      ),
    [items, catFilter, freqFilter]
  );

  const usedCategories = useMemo(
    () => Array.from(new Set(items.map((t) => t.category).filter(Boolean))),
    [items]
  );

  // FASE 17.8 — company admin sees templates grouped by unit
  const groups = useMemo(() => {
    if (!isCompanyAdmin) return null;
    const result = [];
    const allUnitsItems = filtered.filter((t) => !(t.unit_ids || []).length);
    if (allUnitsItems.length) {
      result.push({ key: "all", name: "Todas as unidades", items: allUnitsItems });
    }
    for (const u of units) {
      const uItems = filtered.filter((t) => (t.unit_ids || []).includes(u.id));
      if (uItems.length) result.push({ key: u.id, name: u.name, items: uItems });
    }
    return result;
  }, [filtered, units, isCompanyAdmin]);

  return (
    <Layout
      title="Checklists"
      subtitle="Templates operacionais da sua cozinha"
      actions={
        canManage ? (
          limitInfo.reached ? (
            <Button
              className="bg-stone-300 text-stone-600 cursor-not-allowed"
              disabled
              data-testid="checklist-new"
            >
              <Plus className="h-4 w-4 mr-2" /> Novo template
            </Button>
          ) : (
            <Link to="/checklists/new">
              <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="checklist-new">
                <Plus className="h-4 w-4 mr-2" /> Novo template
              </Button>
            </Link>
          )
        ) : null
      }
    >
      <div data-testid="checklists-page" className="space-y-6">
        <PlanLimitBanner info={limitInfo} label="checklist(s)" />

        {/* Filtros rápidos */}
        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="checklist-filters">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 font-mono mr-1">
              Frequência
            </span>
            {[["all", "Todas"], ["daily", "Diário"], ["weekly", "Semanal"], ["monthly", "Mensal"], ["shift", "Turno"]].map(
              ([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setFreqFilter(v)}
                  className={`rounded-full px-3 py-1 text-xs font-body font-semibold transition-colors ${
                    freqFilter === v
                      ? "bg-emerald-800 text-white"
                      : "bg-white border border-stone-200 text-stone-600 hover:border-emerald-300"
                  }`}
                  data-testid={`checklist-filter-freq-${v}`}
                >
                  {l}
                </button>
              )
            )}
            {usedCategories.length > 1 && (
              <>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 font-mono ml-3 mr-1">
                  Categoria
                </span>
                <button
                  type="button"
                  onClick={() => setCatFilter("all")}
                  className={`rounded-full px-3 py-1 text-xs font-body font-semibold transition-colors ${
                    catFilter === "all"
                      ? "bg-emerald-800 text-white"
                      : "bg-white border border-stone-200 text-stone-600 hover:border-emerald-300"
                  }`}
                  data-testid="checklist-filter-cat-all"
                >
                  Todas
                </button>
                {usedCategories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCatFilter(c)}
                    className={`rounded-full px-3 py-1 text-xs font-body font-semibold transition-colors ${
                      catFilter === c
                        ? "bg-emerald-800 text-white"
                        : "bg-white border border-stone-200 text-stone-600 hover:border-emerald-300"
                    }`}
                    data-testid={`checklist-filter-cat-${c}`}
                  >
                    {catLabels[c] || c}
                  </button>
                ))}
              </>
            )}
            <span className="ml-auto text-xs text-stone-500 font-body">
              {filtered.length} de {items.length} template(s)
            </span>
          </div>
        )}

        {isCompanyAdmin && groups ? (
          groups.length === 0 ? (
            <Card className="rounded-xl border-stone-200 bg-white p-10 text-center text-stone-500 font-body">
              <ClipboardCheck className="h-6 w-6 mx-auto mb-2 text-stone-400" />
              Nenhum template criado
            </Card>
          ) : (
            groups.map((g) => (
              <section key={g.key} data-testid={`checklist-group-${g.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4 text-emerald-700" />
                  <h3 className="font-display text-lg font-bold tracking-tight">{g.name}</h3>
                  <Badge variant="outline" className="border-stone-300 text-stone-600 text-[10px]">
                    {g.items.length}
                  </Badge>
                </div>
                <TemplatesTable
                  items={g.items}
                  catLabels={catLabels}
                  canManage={canManage}
                  onStart={startExecution}
                  onRemove={remove}
                  onEdit={editTemplate}
                  onCopy={copyTemplate}
                />
              </section>
            ))
          )
        ) : (
          <TemplatesTable
            items={filtered}
            catLabels={catLabels}
            canManage={canManage}
            onStart={startExecution}
            onRemove={remove}
            onEdit={editTemplate}
            onCopy={copyTemplate}
          />
        )}
      </div>
    </Layout>
  );
}
